import { useCallback, useEffect } from "react";

import { t } from "i18next";
import type { ResourceType } from "maplibre-gl";
import {
  isMapboxURL,
  transformMapboxUrl,
} from "maplibregl-mapbox-request-transformer";

import { selectConfigItem } from "@/features/config/configSlice";
import { setBaseMapStyle } from "@/features/map/mapSlice";
import {
  AppMessageLevel,
  addMessage,
  selectIsThemeAccessible,
  selectSelectedTheme,
} from "../../appSlice";

import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { MAPBOX_API_KEY } from "@/constants";
import {
  MAPBOXSTYLE_DARK,
  MAPBOXSTYLE_HIGHT_CONTRAST_DARK,
  MAPBOXSTYLE_HIGHT_CONTRAST_LIGHT,
  MAPBOXSTYLE_LIGHT,
} from "@/features/map/constants";
import { AccessibleColorThemes, ColorThemes } from "../../constants";

const useBaseMapStyle = () => {
  const dispatch = useAppDispatch();
  const configBaseMapStyle = useAppSelector(
    selectConfigItem("map.basemap.custom_style_url"),
  );
  const configBaseMapAccesibleStyle = useAppSelector(
    selectConfigItem("map.basemap.custom_style_high_contrast_dark_url"),
  );
  const isThemeAccessible = useAppSelector(selectIsThemeAccessible);
  const selectedTheme = useAppSelector(selectSelectedTheme);

  const handleFallbackBaseMap = useCallback(() => {
    if (isThemeAccessible) {
      dispatch(
        setBaseMapStyle(
          selectedTheme === AccessibleColorThemes.highContrastDark
            ? MAPBOXSTYLE_HIGHT_CONTRAST_DARK
            : MAPBOXSTYLE_HIGHT_CONTRAST_LIGHT,
        ),
      );
      return;
    }
    dispatch(
      setBaseMapStyle(
        selectedTheme === ColorThemes.dark
          ? MAPBOXSTYLE_DARK
          : MAPBOXSTYLE_LIGHT,
      ),
    );
  }, [isThemeAccessible, selectedTheme]);

  const handleCustomBaseMap = useCallback(
    ({
      customBaseMapStyleURL,
      controller,
    }: {
      customBaseMapStyleURL: string;
      controller: AbortController;
    }) => {
      let basemapStyleURL = customBaseMapStyleURL;

      if (isMapboxURL(basemapStyleURL)) {
        basemapStyleURL = transformMapboxUrl(
          basemapStyleURL,
          "Style" as ResourceType,
          MAPBOX_API_KEY,
        ).url;
      }

      // Apply custom style immediately to avoid loading default map
      dispatch(setBaseMapStyle(basemapStyleURL));

      fetch(basemapStyleURL, { signal: controller.signal })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
          return res.json();
        })
        .catch((err: Error) => {
          if (err.name === "AbortError") {
            return;
          }
          // If custom style fails, fallback to default base map
          handleFallbackBaseMap();
          dispatch(
            addMessage({
              id: "map-style-fetch-error",
              level: AppMessageLevel.ERROR,
              message: t("error.map.style_load", {
                reason: err.message,
                url: basemapStyleURL,
              }),
            }),
          );
        });
    },
    [handleFallbackBaseMap],
  );

  // Handle base map style changes
  useEffect(() => {
    const controller = new AbortController();

    // Force accessible theme if configured
    if (isThemeAccessible && configBaseMapAccesibleStyle) {
      handleCustomBaseMap({
        controller,
        customBaseMapStyleURL: configBaseMapAccesibleStyle,
      });
      return;
    }

    // Handle custom style if configured
    if (configBaseMapStyle) {
      handleCustomBaseMap({
        controller,
        customBaseMapStyleURL: configBaseMapStyle,
      });
      return;
    }

    // Handle default base map
    handleFallbackBaseMap();

    return () => controller.abort();
  }, [
    isThemeAccessible,
    configBaseMapStyle,
    configBaseMapAccesibleStyle,
    handleCustomBaseMap,
    handleFallbackBaseMap,
  ]);
};

export default useBaseMapStyle;
